import { Component, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { ImpersonationTargets } from 'src/app/auth/models/impersonation';
import { ImpersonationService } from 'src/app/auth/services/impersonation.service';

type Mode = 'pharmacies' | 'lab';

@Component({
  selector: 'app-impersonation-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule],
  templateUrl: './impersonation-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./impersonation-dialog.component.scss'],
})
export class ImpersonationDialogComponent implements OnInit {
  private readonly service = inject(ImpersonationService);
  private readonly dialogRef = inject(MatDialogRef<ImpersonationDialogComponent>);

  readonly mode = signal<Mode>('pharmacies');
  readonly targets = signal<ImpersonationTargets | null>(null);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal('');

  readonly search = signal('');
  readonly selectedCodes = signal<number[]>([]);
  /** The Bidscircle access level to view with; null keeps the pharmacies' own (the dashboards are filtered by it). */
  readonly accessLevel = signal<number | null>(null);
  readonly labId = signal<number | null>(null);
  readonly contactId = signal<number | null>(null);

  readonly filteredPharmacies = computed(() => {
    const term = this.search().trim().toLowerCase();
    const pharmacies = this.targets()?.pharmacies ?? [];
    if (!term) {
      return pharmacies;
    }
    return pharmacies.filter(
      (p) => p.nome.toLowerCase().includes(term) || String(p.codigoAnf).includes(term),
    );
  });

  readonly contacts = computed(
    () => this.targets()?.labs.find((l) => l.laboratorioId === this.labId())?.contacts ?? [],
  );

  readonly canStart = computed(() =>
    this.mode() === 'pharmacies' ? this.selectedCodes().length > 0 : this.contactId() !== null,
  );

  ngOnInit(): void {
    this.service.targets().subscribe({
      next: (targets) => {
        this.targets.set(targets);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.errorMessage.set(this.messageFor(err, 'Não foi possível carregar a lista de farmácias e laboratórios.'));
      },
    });
  }

  setMode(mode: Mode): void {
    this.mode.set(mode);
    this.errorMessage.set('');
  }

  isSelected(code: number): boolean {
    return this.selectedCodes().includes(code);
  }

  toggle(code: number): void {
    this.selectedCodes.update((codes) =>
      codes.includes(code) ? codes.filter((c) => c !== code) : [...codes, code],
    );
  }

  onLabChange(value: string): void {
    this.labId.set(value ? Number(value) : null);
    this.contactId.set(null);
  }

  onAccessLevelChange(value: string): void {
    this.accessLevel.set(value.trim() === '' ? null : Math.max(0, Math.floor(Number(value) || 0)));
  }

  onContactChange(value: string): void {
    this.contactId.set(value ? Number(value) : null);
  }

  start(): void {
    if (!this.canStart() || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.errorMessage.set('');

    const isPharmacies = this.mode() === 'pharmacies';
    const request = isPharmacies
      ? this.service.startPharmacies(this.selectedCodes(), this.accessLevel())
      : this.service.startLab(this.contactId()!);

    request.subscribe({
      next: () => {
        this.dialogRef.close(true);
        // Full page load so every page reads the data of the new view.
        // A lab contact lands on the lab area; pharmacies on the pharmacy home.
        this.service.goTo(isPharmacies ? '/' : '/lab/home');
      },
      error: (err) => {
        this.saving.set(false);
        this.errorMessage.set(this.messageFor(err, 'Não foi possível iniciar a sessão.'));
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private messageFor(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 403) {
        return 'Não tem permissão para esta ação.';
      }
      if (err.status === 404) {
        return 'A farmácia ou o contacto escolhido já não existe.';
      }
      const body = err.error;
      if (err.status === 400 && typeof body === 'string' && body) {
        return body;
      }
      if (err.status === 400 && body && typeof body.message === 'string') {
        return body.message;
      }
    }
    return fallback;
  }
}
